import { Component, DestroyRef, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { ReusableFormGroupComponent } from "../reusable-form-group/reusable-form-group.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  DatePickerFormValidators,
  DatepickerHelper
} from "../../../shared/validators/date-picker-form-validators";
import { AddressFormGroup, AddressFormGroupComponent } from "../address-form-group/address-form-group.component";
import { CountriesService } from "../../../shared/services/countries.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IndividualData } from "@models/individuals/individual-data";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { Address, countryDefaultValue } from "../../../models/address/address";
import { startWith, take } from "rxjs";
import { parseFullName } from "../../../shared/utils/parse-fullname";
import { CustomFormValidators } from "../../../shared/validators/custom-form-validators";
import { directorFullNameValidatorMsg } from "../../../shared/validators/custom-form-validators-messages";
import { ParsedName } from "@models/individuals/parsed-name";
import {
  IntegrationsEntitySearchControlComponent
} from "../../controls/integrations-entity-search-control/integrations-entity-search-control.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { EntityType } from '@models/common/entity-type.enum';
import {
  SuburbsAutocompleteComponent
} from "../address-form-group/suburbs-autocomplete/suburbs-autocomplete.component";
import { ManualAddressService } from "../../../shared/services/manual-address.service";

export interface IndividualDataFormGroupControls {
  fullName: FormControl<string | null>;
  formerName: FormControl<ParsedName | null>;
  formerFullName: FormControl<string | null>;
  dob: FormControl<Date | null>;
  birthCity: FormControl<string | null>;
  birthCountry: FormControl<string | null>;
  address: AddressFormGroup;
}

export type IndividualDataFormGroup = FormGroup<IndividualDataFormGroupControls>;

@Component({
  selector: 'app-individual-data-form-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    DatePickerComponent,
    AddressFormGroupComponent,
    FlagsAutocompleteComponent,
    SuburbsAutocompleteComponent,
    IntegrationsEntitySearchControlComponent,
],
  templateUrl: './individual-data-form-group.component.html',
  styleUrl: './individual-data-form-group.component.scss',
  providers:[ManualAddressService]
})
export class IndividualDataFormGroupComponent extends ReusableFormGroupComponent<IndividualDataFormGroup> {
  destroyRef = inject(DestroyRef);
  countriesService = inject(CountriesService);

  @Input() useInternationalAddresses = false;
  @Input() suggestedAddresses: Address[] = [];
  @Input() fullNameSuggestions: string[] = [];
  @Input() minDate: NgbDateStruct = DatepickerHelper.getDefaultMinDateStruct();
  @Input() maxDate: NgbDateStruct = DatepickerHelper.getMatureDateStruct();
  @Input() individualsName = 'Officeholders';
  @Input() formerNameVisible = false;
  @Input() readonly = false;

  @Input('individualDataFormLabels') set labels(labels: Record<keyof Partial<IndividualDataFormGroupControls>, string>) {
    this.individualDataFormLabels = Object.assign(this.individualDataFormLabels, labels);
  }

  @Input('hiddenControls') set hideControls(controlsList: (keyof IndividualDataFormGroupControls)[]) {
    controlsList.forEach(controlName => {
      this.hiddenControls[controlName] = true;
    });
  }

  @Output() individualSelected = new EventEmitter<IndividualData>();

  individualDataFormLabels: Record<keyof IndividualDataFormGroupControls, string> = {
    fullName: 'Full Name',
    formerName: 'Former Name (if any)',
    formerFullName: 'Former Name (if any)',
    dob: 'Date of Birth',
    birthCity: 'Suburb or City of Birth',
    birthCountry: 'Country or State of Birth',
    address: 'Address',
  };
  hiddenControls: Record<string | (keyof IndividualDataFormGroupControls), boolean> = {};
  countryOptions = this.countriesService.getCountriesAndDivisions();
  entityType = EntityType.Individual;
  expandedAddressForm = false;
  isAustraliaSelected = signal(false);
  readonly directorFullNameValidatorMsg = directorFullNameValidatorMsg;

  override ngOnInit() {
    super.ngOnInit();
    this.listenBirthCountryChanges();
    this.defineFormerNameVisibility();
  }

  private listenBirthCountryChanges(): void {
    this.form.controls.birthCountry.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(this.form.controls.birthCountry.value))
      .subscribe((birthCountry) => {
        if (birthCountry) {
          this.isAustraliaSelected.set(CountriesService.isAustralianStateOrAustralia(birthCountry));

          if (this.isAustraliaSelected()) {
            this.form.controls.birthCity.setValidators([Validators.required]);
          } else {
            this.form.controls.birthCity.clearValidators();
          }

          this.form.controls.birthCity.patchValue(this.form.controls.birthCity.getRawValue() ?? null);
          this.form.updateValueAndValidity();
          return;
        }

        this.isAustraliaSelected.set(false);
      });
  }

  private defineFormerNameVisibility(): void {
    // Show Former Name if it has value or if control is already visible
    this.form.valueChanges
      .pipe(take(1))
      .subscribe(({ formerName }) => this.formerNameVisible = !!formerName || this.formerNameVisible);
  }

  onIndividualSelected($event: IndividualData) {
    if ($event.address) {
      $event.address.country = $event.address.country || countryDefaultValue;
    }

    this.form.controls.dob.patchValue($event.dob ?? null);
    this.form.controls.address.patchValue($event.address);
    this.form.controls.birthCity.patchValue($event.birthCity ?? null);
    this.form.controls.birthCountry.patchValue($event.birthCountry ?? null);

    this.individualSelected.emit($event);
  }

  get dateOfBirthCustomErrors() {
    return { tooBigDate: `${ this.individualsName } must be at least 18 years of age.`, tooSmallDate: DatePickerFormValidators.errorMessages.tooSmallDate };
  }

  static override defineForm(): IndividualDataFormGroup {
    return new FormGroup(IndividualDataFormGroupComponent.defineFormControls());
  }

  static override defineFormControls(): IndividualDataFormGroupControls {
    return {
      fullName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.fullNameValidator]),
      formerName: new FormControl<ParsedName | null>(null),
      formerFullName: new FormControl<string | null>(null, [CustomFormValidators.fullNameValidator]),
      dob: new FormControl<Date | null>(null, [Validators.required]),
      birthCity: new FormControl<string | null>(null, [Validators.required]),
      birthCountry: new FormControl<string | null>(null, [Validators.required]),
      address: AddressFormGroupComponent.defineForm()
    };
  }

  static toIndividualData(form: FormGroup<Partial<IndividualDataFormGroupControls>>, individualData: Partial<IndividualData> = {}): IndividualData {
    const formerName = parseFullName(form?.controls?.formerFullName?.value ?? '') as ParsedName;

    return new IndividualData({
      ...individualData,
      ...form.value as Partial<IndividualData>,
      ...parseFullName(form?.controls?.fullName?.value ?? ''),
      formerName,
    });
  }
}
